import { lazy, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { BrowserRouter, Route, Switch } from 'react-router-dom';

import { AutomatedQuoteGeneration } from 'app/modules/AutomatedQuoteGeneration';
import { CurrencyProvider } from 'app/modules/CurrencyProvider';
import { NeedItSooner } from 'app/modules/NeedItSooner';
import { Payment3DSFinalized } from 'app/modules/Payment3DSFinalized';
import { ThankYouPage } from 'app/modules/ThankYouPage';
import { appPaths, routes } from 'app/routes';
import { LinearLoader } from 'app/theme/LinearLoader';
import { AlertsProvider } from 'pkg/Alerts';
import { HttpErrorFallback } from 'pkg/components/ErrorHandling/HttpErrorFallback';
import { RouteErrorBoundary } from 'pkg/components/ErrorHandling/RouteErrorBoundary';
import { RuntimeErrorFallback } from 'pkg/components/ErrorHandling/RuntimeErrorFallback';
import { NotFound } from 'pkg/components/NotFound';
import { Layout } from 'pkg/components/layout';

import { ThemeProvider } from '../theme/ThemeProvider';
import { theme } from '../theme/theme';
import Context from './Context';
import { Header } from './Header/Header';
import { QueryCacheProvider } from './QueryCacheProvider';

const PaymentWithLink = lazy(() => import('app/modules/PaymentWithLink'));
const AudioTranscriptionQuote = lazy(() => import('./AudioTranscriptionQuote'));
const CopywritingQuote = lazy(() => import('./CopywritingQuote'));
const InstantQuote = lazy(() => import('./InstantQuote'));
const InterpretingQuote = lazy(() => import('./InterpretingQuote'));
const MultilingualDTPQuote = lazy(() => import('./MultilingualDTPQuote'));
const ProofreadingQuote = lazy(() => import('./ProofreadingQuote'));
const SubtitlingQuote = lazy(() => import('./SubtitlingQuote'));
const TransactionStatus = lazy(() => import('./TransactionStatus'));
const TranslationQuote = lazy(() => import('./TranslationQuote'));
const VoiceOverQuote = lazy(() => import('./VoiceOverQuote'));
const Payment = lazy(() => import('./Payment'));
const InvoicePortal = lazy(() => import('./InvoicePortal'));

const PAYMENT_PATH = `${routes.PAYMENT}/:customerId/:quoteId/:totalPrice/:currency/:option?`;
const PAYMENT_FINALIZATION = `${routes.PAYMENT_FINALIZATION}/:customerId/:quoteId/:totalPrice/:currency/:option?`;

const paths = [...appPaths, `${PAYMENT_PATH}/:option`, PAYMENT_PATH, PAYMENT_FINALIZATION];

export const App = (): JSX.Element => (
  <ThemeProvider theme={theme}>
    <ErrorBoundary FallbackComponent={RuntimeErrorFallback}>
      <QueryCacheProvider>
        <CurrencyProvider>
          <Context>
            <BrowserRouter basename={process.env.PUBLIC_URL}>
              <AlertsProvider>
                <Layout header={<Header />}>
                  <RouteErrorBoundary FallbackComponent={HttpErrorFallback}>
                    <Suspense fallback={<LinearLoader $isLoading />}>
                      <Switch>
                        <Route exact component={InstantQuote} path={routes.INSTANT_QUOTE} />
                        <Route path={paths}>
                          <Route component={ProofreadingQuote} path={routes.PROOFREADING_QUOTE} />
                          <Route component={CopywritingQuote} path={routes.COPYWRITING_QUOTE} />
                          <Route component={SubtitlingQuote} path={routes.SUBTITLING_QUOTE} />
                          <Route component={VoiceOverQuote} path={routes.VOICE_OVER_QUOTE} />
                          <Route component={InterpretingQuote} path={routes.INTERPRETING_QUOTE} />
                          <Route component={MultilingualDTPQuote} path={routes.MULTILINGUALDTP_QUOTE} />
                          <Route component={AudioTranscriptionQuote} path={routes.AUDIO_TRANSCRIPTION_QUOTE} />
                          <Route component={TranslationQuote} path={routes.TRANSLATION_QUOTE.ROOT} />
                          <Route component={PaymentWithLink} path={PAYMENT_PATH} />
                          <Route component={Payment} path={PAYMENT_FINALIZATION} />
                          <Route component={TransactionStatus} path={`*${routes.TRANSACTION_STATUS}/:transactionId`} />
                          <Route component={ThankYouPage} path={`*${routes.THANK_YOU}/:id`} />
                          <Route component={NeedItSooner} path={`*${routes.NEED_IT_SOONER}/:id/:option`} />
                          <Route
                            component={AutomatedQuoteGeneration}
                            path={`*${routes.AUTOMATED_QUOTE_GENERATION}/:id`}
                          />
                          <Route component={InvoicePortal} path={routes.INVOICE_PORTAL.ROOT} />
                        </Route>
                        <Route component={Payment3DSFinalized} path={routes.PAYMENT_3DS_FINALIZATION} />
                        <Route component={NotFound} />
                      </Switch>
                    </Suspense>
                  </RouteErrorBoundary>
                </Layout>
              </AlertsProvider>
            </BrowserRouter>
          </Context>
        </CurrencyProvider>
      </QueryCacheProvider>
    </ErrorBoundary>
  </ThemeProvider>
);
